import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { MultiColumnList, Pane, Paneset, Layout, Headline, Loading, KeyValue, Badge, ButtonGroup, Button, } from '@folio/stripes/components';
import useImportBatchDetail from '../hooks/useImportBatchDetail';
import useImportBatchItemDetail from '../hooks/useImportBatchItemDetail';
import MarcDataDisplay from '../components/RecordDetail/MarcDataDisplay';
import NavPaneLayout from '../components/NavPaneLayout';
import ImportProtocolSection from '../components/ImportProtocol/ImportProtocolSection';
import { TableLoadingSkeleton, TableRefetchOverlay } from '../components/TableStates';
/**
 * Pretty-print a one-line XML string with indentation.
 * Uses a simple regex approach — no external dependency needed.
 */
var formatXml = function (xml) {
    var formatted = '';
    var indent = 0;
    var parts = xml.replace(/>\s*</g, '><').split(/(<[^>]+>)/).filter(function (part) { return part.trim(); });
    for (var _i = 0, parts_1 = parts; _i < parts_1.length; _i++) {
        var part = parts_1[_i];
        if (part.startsWith('<?')) {
            formatted += part + '\n';
        }
        else if (part.startsWith('</')) {
            indent--;
            formatted += '  '.repeat(Math.max(0, indent)) + part + '\n';
        }
        else if (part.startsWith('<') && part.endsWith('/>')) {
            formatted += '  '.repeat(indent) + part + '\n';
        }
        else if (part.startsWith('<') && !part.startsWith('</')) {
            formatted += '  '.repeat(indent) + part + '\n';
            indent++;
        }
        else {
            // Text content — append to previous line
            formatted = formatted.trimEnd() + part + '\n';
        }
    }
    return formatted.trimEnd();
};
var KNOWN_ERROR_CODES = new Set(['VALIDATION_ERROR', 'MAPPING_ERROR', 'PERSISTENCE_ERROR', 'DUPLICATE_CONFLICT']);
var ImportBatchDetail = function () {
    var intl = useIntl();
    var history = useHistory();
    var match = useRouteMatch();
    var _a = useParams(), batchId = _a.batchId, itemId = _a.itemId;
    var _b = useState('structured'), activeTab = _b[0], setActiveTab = _b[1];
    var detail = useImportBatchDetail(batchId);
    var itemDetail = useImportBatchItemDetail(batchId, itemId || '');
    // Navigate back to batch list
    var parentPath = match.url.replace(new RegExp("/".concat(batchId, ".*$")), '');
    var handleClose = function () {
        history.push(parentPath);
    };
    var handleRowClick = useCallback(function (_e, row) {
        history.push("".concat(parentPath, "/").concat(batchId, "/items/").concat(row.id));
    }, [parentPath, batchId, history]);
    var handleItemClose = function () {
        history.push("".concat(parentPath, "/").concat(batchId));
    };
    var visibleColumns = useMemo(function () { return [
        'sourceRecordId',
        'errorCode',
        'errorMessage',
    ]; }, []);
    var columnMapping = useMemo(function () { return ({
        sourceRecordId: React.createElement(FormattedMessage, { id: "ui-czna-authorities.imports.detail.column.sourceRecordId" }),
        errorCode: React.createElement(FormattedMessage, { id: "ui-czna-authorities.imports.detail.column.errorCode" }),
        errorMessage: React.createElement(FormattedMessage, { id: "ui-czna-authorities.imports.detail.column.errorMessage" }),
    }); }, []);
    var resultsFormatter = useMemo(function () { return ({
        sourceRecordId: function (record) { return record.sourceRecordId; },
        errorCode: function (record) { return record.errorCode
            ? intl.formatMessage({ id: "ui-czna-authorities.imports.errorCode.".concat(record.errorCode) })
            : '—'; },
        errorMessage: function (record) { return record.errorMessage || '—'; },
    }); }, [intl]);
    var renderListContent = function () {
        // CZNA-538 — see AuthoritiesList.
        if (detail.isLoading && detail.items.length === 0) {
            return React.createElement(TableLoadingSkeleton, { testId: "import-items-loading", columns: [3, 6, 3, 5], rows: 6 });
        }
        if (detail.isError) {
            return (React.createElement(Layout, { className: "display-flex flex-direction-column centerContent" },
                React.createElement(Layout, { className: "padding-all-gutter" },
                    React.createElement(Headline, { size: "medium", margin: "none" },
                        React.createElement(FormattedMessage, { id: "error.boundary.heading" })))));
        }
        if (detail.items.length === 0 && !detail.isLoading) {
            return (React.createElement(Layout, { className: "display-flex flex-direction-column centerContent" },
                React.createElement(Layout, { className: "padding-all-gutter" },
                    React.createElement(Headline, { size: "medium", margin: "none" },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.imports.detail.empty" })))));
        }
        return (
        /* CZNA-538 — see AuthoritiesList: the overlay replaces MCL's corner spinner. */
        React.createElement(TableRefetchOverlay, { active: detail.isLoading, testId: "import-items-refetching" },
            React.createElement(MultiColumnList, { autosize: true, virtualize: true, contentData: detail.items, totalCount: detail.totalRecords, pagingType: "prev-next", pagingOffset: detail.offset, pageAmount: detail.pageSize, columnMapping: columnMapping, visibleColumns: visibleColumns, formatter: resultsFormatter, onRowClick: handleRowClick, onNeedMoreData: function (_amount, offset) { return detail.setOffset(offset); } })));
    };
    // === Item detail side panel content ===
    var renderNoSourceData = function () { return (React.createElement(Layout, { className: "display-flex flex-direction-column centerContent" },
        React.createElement(Layout, { className: "padding-all-gutter" },
            React.createElement(Headline, { size: "medium", margin: "none" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.imports.item.noSourceData" }))))); };
    var renderItemDetail = function () {
        var data = itemDetail.data;
        if (itemDetail.isLoading) {
            return (React.createElement(Layout, { className: "display-flex flex-direction-column centerContent" },
                React.createElement(Layout, { className: "padding-all-gutter" },
                    React.createElement(Loading, { size: "xlarge" }))));
        }
        if (itemDetail.isError || !data) {
            return (React.createElement(Layout, { className: "display-flex flex-direction-column centerContent" },
                React.createElement(Layout, { className: "padding-all-gutter" },
                    React.createElement(Headline, { size: "medium", margin: "none" },
                        React.createElement(FormattedMessage, { id: "error.boundary.heading" })))));
        }
        var hasSourceData = data.sourceRecordXml || data.dataFields;
        return (React.createElement(React.Fragment, null,
            React.createElement(Layout, { className: "padding-bottom-gutter" },
                React.createElement("div", { style: { display: 'flex', gap: '2rem', flexWrap: 'wrap' } },
                    React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-authorities.imports.item.sourceRecordId" }), value: React.createElement("span", { style: { fontFamily: 'monospace', fontWeight: 600 } }, data.sourceRecordId) }),
                    React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-authorities.imports.item.errorCode" }), value: data.errorCode ? (React.createElement(Badge, { color: KNOWN_ERROR_CODES.has(data.errorCode) ? 'red' : 'default' }, intl.formatMessage({ id: "ui-czna-authorities.imports.errorCode.".concat(data.errorCode) }))) : '—' })),
                React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-authorities.imports.item.errorMessage" }), value: data.errorMessage || '—' })),
            React.createElement("hr", { style: { border: 'none', borderTop: '1px solid #e4e4e7', margin: '1rem 0' } }),
            !hasSourceData && renderNoSourceData(),
            hasSourceData && (React.createElement(React.Fragment, null,
                React.createElement(Layout, { className: "padding-bottom-gutter" },
                    React.createElement(ButtonGroup, null,
                        React.createElement(Button, { buttonStyle: activeTab === 'structured' ? 'primary' : 'default', onClick: function () { return setActiveTab('structured'); } },
                            React.createElement(FormattedMessage, { id: "ui-czna-authorities.imports.item.section.structured" })),
                        React.createElement(Button, { buttonStyle: activeTab === 'marcxml' ? 'primary' : 'default', onClick: function () { return setActiveTab('marcxml'); } },
                            React.createElement(FormattedMessage, { id: "ui-czna-authorities.imports.item.section.marcxml" })))),
                activeTab === 'structured' && data.dataFields && (React.createElement(MarcDataDisplay, { dataFields: data.dataFields })),
                activeTab === 'structured' && !data.dataFields && renderNoSourceData(),
                activeTab === 'marcxml' && data.sourceRecordXml && (React.createElement("pre", { style: {
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px',
                        padding: '1rem',
                        overflow: 'auto',
                        fontSize: '0.85rem',
                        fontFamily: "'SF Mono', 'Cascadia Code', 'Consolas', monospace",
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                    } }, formatXml(data.sourceRecordXml))),
                activeTab === 'marcxml' && !data.sourceRecordXml && renderNoSourceData()))));
    };
    return (React.createElement(Paneset, null,
        React.createElement(Pane, { defaultWidth: "20%", paneTitle: React.createElement(FormattedMessage, { id: "ui-czna-authorities.pane.navigation" }) },
            React.createElement(NavPaneLayout, null)),
        React.createElement(Pane, { defaultWidth: "fill", paneTitle: React.createElement(FormattedMessage, { id: "ui-czna-authorities.imports.detail.title" }), paneSub: detail.totalRecords > 0
                ? intl.formatMessage({ id: 'ui-czna-authorities.imports.detail.failedRecordsCount' }, { count: detail.totalRecords })
                : undefined, dismissible: true, onClose: handleClose },
            React.createElement(ImportProtocolSection, { batchId: batchId }),
            React.createElement(Headline, { size: "large", margin: "small" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.imports.detail.failedRecords" })),
            renderListContent()),
        itemId && (React.createElement(Pane, { defaultWidth: "44%", paneTitle: React.createElement(FormattedMessage, { id: "ui-czna-authorities.imports.item.title" }), dismissible: true, onClose: handleItemClose }, renderItemDetail()))));
};
export default ImportBatchDetail;
